import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageNavigation from '../components/PageNavigation.jsx';

export default function EchoChamberInfo() {
  const navigate = useNavigate();

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #000000 0%, #1a0033 50%, #000000 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.2,
        background: 'radial-gradient(circle at 20% 50%, rgba(147,51,234,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(99,102,241,0.15) 0%, transparent 50%)',
        animation: 'pulse 8s ease-in-out infinite'
      }} />

      <PageNavigation backTo="/echo-chamber" backLabel="← Back to Echo Chamber" />

      <div style={{ 
        maxWidth: 1000, 
        margin: '0 auto', 
        padding: '60px 20px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{ 
            fontSize: 80, 
            marginBottom: 24,
            textShadow: '0 0 40px rgba(147,51,234,0.8)'
          }}>
            🌌
          </div>
          <h1 style={{ 
            color: '#c9a62b', 
            fontSize: 48, 
            marginBottom: 20,
            fontFamily: 'Arsenica',
            textShadow: '0 2px 30px rgba(201,166,43,0.6)'
          }}>
            Understanding the Echo Chamber
          </h1>
          <p style={{ 
            color: '#d1d5db', 
            fontSize: 18, 
            lineHeight: 1.8,
            maxWidth: 800,
            margin: '0 auto'
          }}>
            A living emotional bridge where past, present, and future voices interact through AI that understands feeling, not just words.
          </p>
        </div>

        {/* Core Purpose */}
        <section style={{
          background: 'linear-gradient(135deg, rgba(147,51,234,0.15) 0%, rgba(147,51,234,0.05) 100%)',
          border: '2px solid rgba(147,51,234,0.3)',
          borderRadius: 20,
          padding: 40,
          marginBottom: 48,
          boxShadow: '0 10px 40px rgba(147,51,234,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <span style={{ fontSize: 48 }}>🌌</span>
            <h2 style={{ 
              color: '#c9a62b', 
              fontSize: 32, 
              margin: 0,
              fontFamily: 'Arsenica'
            }}>
              Core Purpose
            </h2>
          </div>
          <p style={{ 
            color: '#e5e7eb', 
            fontSize: 18, 
            lineHeight: 1.8,
            marginBottom: 0
          }}>
            The Echo Chamber isn't just a playback tool. It's a <strong style={{ color: '#c9a62b' }}>living emotional bridge</strong> — where past, present, and future voices interact through AI that understands <em>feeling</em>, not just words.
          </p>
        </section>

        {/* How It Works - 4 Key Layers */}
        <section style={{ marginBottom: 48 }}>
          <h2 style={{ 
            color: '#c9a62b', 
            fontSize: 36, 
            marginBottom: 32,
            fontFamily: 'Arsenica',
            textAlign: 'center'
          }}>
            🎙️ How It Works – The 4 Key Layers
          </h2>

          <div style={{ display: 'grid', gap: 32 }}>
            {/* Layer 1 */}
            <div style={{
              background: 'rgba(0,0,0,0.4)',
              border: '2px solid rgba(99,102,241,0.3)',
              borderRadius: 20,
              padding: 32
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: '#fff'
                }}>1</div>
                <h3 style={{ 
                  color: '#6366f1', 
                  fontSize: 26,
                  margin: 0,
                  fontFamily: 'Arsenica'
                }}>
                  Voice Activated (Natural Input)
                </h3>
              </div>
              <div style={{ paddingLeft: 64 }}>
                <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  Users can speak naturally — no scripts, no rigid phrases.
                </p>
                <div style={{
                  background: 'rgba(99,102,241,0.1)',
                  borderLeft: '3px solid #6366f1',
                  borderRadius: '0 12px 12px 0',
                  padding: 16,
                  marginBottom: 16
                }}>
                  <p style={{ color: '#c7d2fe', fontSize: 15, fontStyle: 'italic', margin: 0 }}>
                    "I miss Mom's laugh during monsoon nights."<br/>
                    <em>Or type:</em> "Tell me what Dad would say if he saw me now."
                  </p>
                </div>
                <p style={{ color: '#bdbdbd', fontSize: 15, lineHeight: 1.7, margin: 0 }}>
                  The system listens like a friend — not a machine.
                </p>
              </div>
            </div>

            {/* Layer 2 */}
            <div style={{
              background: 'rgba(0,0,0,0.4)',
              border: '2px solid rgba(147,51,234,0.3)',
              borderRadius: 20,
              padding: 32
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #9333ea, #7c3aed)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: '#fff'
                }}>2</div>
                <h3 style={{ 
                  color: '#9333ea', 
                  fontSize: 26,
                  margin: 0,
                  fontFamily: 'Arsenica'
                }}>
                  AI Powered (Emotion + Context Engine)
                </h3>
              </div>
              <div style={{ paddingLeft: 64 }}>
                <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  Your <strong style={{ color: '#c9a62b' }}>FOZZIL AI</strong> uses:
                </p>
                <ul style={{ color: '#bdbdbd', fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>
                  <li><strong style={{ color: '#9333ea' }}>Adaptive Learning</strong> from your family's voice patterns, tone, values, and emotional history.</li>
                  <li><strong style={{ color: '#9333ea' }}>Emotional Intelligence Engine</strong> to detect sadness, joy, nostalgia, or pride.</li>
                </ul>
                <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  It doesn't just match keywords — it <em style={{ color: '#c9a62b' }}>feels the moment</em>.
                </p>
                <div style={{
                  background: 'rgba(147,51,234,0.1)',
                  borderLeft: '3px solid #9333ea',
                  borderRadius: '0 12px 12px 0',
                  padding: 20
                }}>
                  <p style={{ color: '#c4b5fd', fontSize: 15, lineHeight: 1.8, marginBottom: 12 }}>
                    If you say <strong>"I just made my first curry"</strong>, it doesn't just reply with a memory — it says:
                  </p>
                  <p style={{ color: '#e9d5ff', fontSize: 16, fontStyle: 'italic', marginBottom: 12 }}>
                    "Your mom would've said, 'It's not perfect — but it's yours. That's what matters.'"
                  </p>
                  <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>
                    🎵 Then plays a 3-second clip of her saying it.
                  </p>
                </div>
              </div>
            </div>

            {/* Layer 3 */}
            <div style={{
              background: 'rgba(0,0,0,0.4)',
              border: '2px solid rgba(168,85,247,0.3)',
              borderRadius: 20,
              padding: 32
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #a855f7, #9333ea)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: '#fff'
                }}>3</div>
                <h3 style={{ 
                  color: '#a855f7', 
                  fontSize: 26,
                  margin: 0,
                  fontFamily: 'Arsenica'
                }}>
                  Timeless Connection (Past → Present → Future)
                </h3>
              </div>
              <div style={{ paddingLeft: 64 }}>
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ color: '#a855f7', fontSize: 18, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>👴🏻</span> Past Voices (recordings from ancestors)
                  </h4>
                  <ul style={{ color: '#bdbdbd', fontSize: 15, lineHeight: 1.7 }}>
                    <li>Stored, encrypted, tagged by emotion, relationship, and event.</li>
                    <li>AI learns their tone, rhythm, humor, and wisdom.</li>
                  </ul>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ color: '#a855f7', fontSize: 18, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>🎤</span> Present Reflections (your voice now)
                  </h4>
                  <ul style={{ color: '#bdbdbd', fontSize: 15, lineHeight: 1.7 }}>
                    <li>Recorded in real time — short, emotional, raw.</li>
                    <li>AI tags it: "Nostalgia," "Pride," "Grief."</li>
                  </ul>
                </div>

                <div>
                  <h4 style={{ color: '#a855f7', fontSize: 18, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>👶🏻</span> Future Messages (from kids, future you)
                  </h4>
                  <ul style={{ color: '#bdbdbd', fontSize: 15, lineHeight: 1.7 }}>
                    <li>Set to unlock on a date (e.g., 2075).</li>
                    <li>Can be voice notes like: "Grandpa, I'm proud of the legacy you built."</li>
                    <li>AI remembers them and uses them in future echoes.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 4 */}
            <div style={{
              background: 'rgba(0,0,0,0.4)',
              border: '2px solid rgba(201,166,43,0.3)',
              borderRadius: 20,
              padding: 32
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #d4af37, #c9a02b)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: '#000'
                }}>4</div>
                <h3 style={{ 
                  color: '#c9a62b', 
                  fontSize: 26,
                  margin: 0,
                  fontFamily: 'Arsenica'
                }}>
                  Emotional Response (Not Just Answers — Echoes)
                </h3>
              </div>
              <div style={{ paddingLeft: 64 }}>
                <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.8, marginBottom: 20 }}>
                  The AI doesn't give facts. It gives <strong style={{ color: '#c9a62b' }}>emotional resonance</strong>.
                </p>
                <div style={{
                  background: 'rgba(201,166,43,0.1)',
                  border: '1px solid rgba(201,166,43,0.3)',
                  borderRadius: 16,
                  padding: 24
                }}>
                  <p style={{ color: '#e5e7eb', fontSize: 16, marginBottom: 16 }}>
                    <strong>You say:</strong> <span style={{ color: '#c9a62b', fontStyle: 'italic' }}>"I miss Dad."</span>
                  </p>
                  <div style={{
                    background: 'rgba(0,0,0,0.4)',
                    borderLeft: '3px solid #c9a62b',
                    borderRadius: '0 12px 12px 0',
                    padding: 20
                  }}>
                    <p style={{ color: '#fde68a', fontSize: 15, marginBottom: 12 }}>
                      <strong>AI responds:</strong>
                    </p>
                    <p style={{ color: '#e5e7eb', fontSize: 16, fontStyle: 'italic', marginBottom: 12 }}>
                      "He'd say, 'Don't cry, love — I'm still here in every hug.'"
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: 14, margin: 0 }}>
                      🎵 Then plays a 2-second clip of him saying it — from a real recording, perfectly synced.
                    </p>
                  </div>
                </div>
                <p style={{ 
                  color: '#c9a62b', 
                  fontSize: 18, 
                  fontStyle: 'italic',
                  textAlign: 'center',
                  marginTop: 24,
                  marginBottom: 0
                }}>
                  It's not a reply — it's a memory returning.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Flow */}
        <section style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(99,102,241,0.1) 100%)',
          border: '2px solid rgba(99,102,241,0.3)',
          borderRadius: 20,
          padding: 40,
          marginBottom: 48
        }}>
          <h2 style={{ 
            color: '#c9a62b', 
            fontSize: 32, 
            marginBottom: 32,
            fontFamily: 'Arsenica',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <span>🔧</span> Technical Flow (Behind the Scenes)
          </h2>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 16,
            position: 'relative'
          }}>
            {[
              { text: 'You speak or type', icon: '🎤' },
              { text: 'Voice is processed (noise removal, emotion detection)', icon: '🔊' },
              { text: 'AI checks: Who\'s being spoken to? What emotion? What context?', icon: '🤖' },
              { text: 'AI searches past recordings, family values, future messages', icon: '🔍' },
              { text: 'It crafts a personalized emotional echo — voice + memory + wisdom', icon: '✨' },
              { text: 'Plays back in natural, warm tone — like a memory returning', icon: '🎵' }
            ].map((step, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  minWidth: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20
                }}>
                  {step.icon}
                </div>
                <div style={{
                  flex: 1,
                  background: 'rgba(99,102,241,0.1)',
                  border: '1px solid rgba(99,102,241,0.2)',
                  borderRadius: 12,
                  padding: 16,
                  color: '#e5e7eb',
                  fontSize: 15
                }}>
                  {step.text}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Unique Features */}
        <section style={{ marginBottom: 48 }}>
          <h2 style={{ 
            color: '#c9a62b', 
            fontSize: 36, 
            marginBottom: 32,
            fontFamily: 'Arsenica',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12
          }}>
            <span>💡</span> Unique Features You Can Add
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24 
          }}>
            {[
              {
                icon: '🎯',
                title: 'Echo Match',
                desc: 'If your voice tone matches a past relative\'s, the AI says: "You sound just like Dad when he was 25."',
                color: '#6366f1'
              },
              {
                icon: '📊',
                title: 'Emotion Timeline',
                desc: 'See how your family\'s emotional state has changed over decades.',
                color: '#9333ea'
              },
              {
                icon: '🎡',
                title: 'Wisdom Wheel',
                desc: 'Spin a digital wheel to get a random life lesson from your ancestors.',
                color: '#a855f7'
              },
              {
                icon: '⏰',
                title: 'Memory Trigger',
                desc: 'Set a reminder like "On your 60th birthday, play this echo."',
                color: '#c9a62b'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}05)`,
                border: `2px solid ${feature.color}40`,
                borderRadius: 16,
                padding: 28,
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = `${feature.color}80`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = `${feature.color}40`;
              }}>
                <div style={{ fontSize: 48, marginBottom: 16, textAlign: 'center' }}>
                  {feature.icon}
                </div>
                <h4 style={{ 
                  color: feature.color, 
                  fontSize: 20, 
                  marginBottom: 12,
                  fontFamily: 'Arsenica',
                  textAlign: 'center'
                }}>
                  {feature.title}
                </h4>
                <p style={{ color: '#bdbdbd', fontSize: 14, lineHeight: 1.7, textAlign: 'center', margin: 0 }}>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Why It's Different */}
        <section style={{
          background: 'linear-gradient(135deg, rgba(201,166,43,0.2) 0%, rgba(201,166,43,0.05) 100%)',
          border: '2px solid rgba(201,166,43,0.4)',
          borderRadius: 24,
          padding: 48,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 64, marginBottom: 24 }}>✅</div>
          <h2 style={{ 
            color: '#c9a62b', 
            fontSize: 36, 
            marginBottom: 24,
            fontFamily: 'Arsenica'
          }}>
            Why It's Different
          </h2>
          <p style={{ 
            color: '#e5e7eb', 
            fontSize: 22, 
            lineHeight: 1.8,
            maxWidth: 800,
            margin: '0 auto 32px',
            fontStyle: 'italic'
          }}>
            This isn't a voice memo.
          </p>
          <p style={{ 
            color: '#fde68a', 
            fontSize: 24, 
            lineHeight: 1.8,
            maxWidth: 800,
            margin: '0 auto',
            fontWeight: 'bold'
          }}>
            It's a conversation that grows with time — where your family's soul speaks across generations.
          </p>
        </section>

        {/* CTA Button */}
        <div style={{ textAlign: 'center', marginTop: 60 }}>
          <button
            onClick={() => navigate('/echo-chamber')}
            style={{
              borderRadius: '9999px',
              background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)',
              border: 'none',
              padding: '20px 60px',
              fontSize: 18,
              fontWeight: 'bold',
              color: '#fff',
              boxShadow: '0 12px 32px rgba(147,51,234,0.5)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 16
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 16px 40px rgba(147,51,234,0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(147,51,234,0.5)';
            }}
          >
            <span style={{ fontSize: 24 }}>🌌</span>
            Enter the Echo Chamber
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
